import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePortfolio } from '../hooks/usePortfolio';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { formatCurrency, formatPercent } from '../lib/utils';
import { PremiumPrompt } from '../components/PremiumPrompt';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const mockCurrentPrices = { AAPL: 175.5, MSFT: 410.2, TSLA: 170.0 };

export function Portfolio() {
  const { isPremium } = useAuth();
  const { holdings, loading, addHolding, removeHolding } = usePortfolio();
  const [showAdd, setShowAdd] = useState(false);
  const [newHolding, setNewHolding] = useState({ symbol: '', shares: '', price: '' });

  const portfolioData = holdings.map((item) => {
    const symbol = item.ticker;
    const shares = Number(item.shares);
    const purchasePrice = Number(item.purchase_price);
    const currentPrice = mockCurrentPrices[symbol] || purchasePrice * 1.05;
    const totalValue = currentPrice * shares;
    const totalCost = purchasePrice * shares;
    const gainLoss = totalValue - totalCost;
    const gainLossPercent = totalCost ? gainLoss / totalCost : 0;

    return {
      id: item.id,
      symbol,
      shares,
      price: purchasePrice,
      currentPrice,
      totalValue,
      gainLoss,
      gainLossPercent,
    };
  });

  const totalPortfolioValue = portfolioData.reduce((acc, curr) => acc + curr.totalValue, 0);
  const totalPortfolioCost = portfolioData.reduce(
    (acc, curr) => acc + curr.price * curr.shares,
    0
  );
  const totalGainLoss = totalPortfolioValue - totalPortfolioCost;
  const totalGainLossPercent = totalPortfolioCost > 0 ? totalGainLoss / totalPortfolioCost : 0;

  const handleAdd = async (e) => {
    e.preventDefault();
    if (newHolding.symbol && newHolding.shares && newHolding.price) {
      await addHolding({
        symbol: newHolding.symbol.toUpperCase(),
        shares: newHolding.shares,
        price: newHolding.price,
      });
      setShowAdd(false);
      setNewHolding({ symbol: '', shares: '', price: '' });
    }
  };

  const chartData = portfolioData.map((d) => ({
    name: d.symbol,
    Valor: d.totalValue,
    color: d.gainLoss >= 0 ? '#007A4D' : '#8B0000',
  }));

  if (loading && !holdings.length) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-10 w-48 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-brand-dark">Mi Portafolio</h1>
        <Button onClick={() => setShowAdd(!showAdd)} className="gap-2">
          <SafeIcon icon={showAdd ? FiIcons.FiX : FiIcons.FiPlus} />
          {showAdd ? 'Cancelar' : 'Agregar Activo'}
        </Button>
      </div>

      {!isPremium && <PremiumPrompt />}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Valor Total</p>
          <p className="text-3xl font-bold text-brand-dark">{formatCurrency(totalPortfolioValue)}</p>
        </Card>
        <Card className="p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Ganancia / Pérdida Total</p>
          <p
            className={`text-3xl font-bold ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            {totalGainLoss > 0 ? '+' : ''}
            {formatCurrency(totalGainLoss)}
          </p>
        </Card>
        <Card className="p-6">
          <p className="text-sm font-medium text-gray-500 mb-1">Rendimiento</p>
          <p
            className={`text-3xl font-bold flex items-center gap-2 ${totalGainLossPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            <SafeIcon
              icon={totalGainLossPercent >= 0 ? FiIcons.FiTrendingUp : FiIcons.FiTrendingDown}
            />
            {formatPercent(totalGainLossPercent)}
          </p>
        </Card>
      </div>

      {showAdd && (
        <Card className="p-6 bg-gray-50 border-dashed border-2">
          <form onSubmit={handleAdd} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Símbolo</label>
              <input
                required
                type="text"
                placeholder="ej. AAPL"
                className="w-full px-4 py-2 border rounded-md"
                value={newHolding.symbol}
                onChange={(e) => setNewHolding({ ...newHolding, symbol: e.target.value })}
              />
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Acciones</label>
              <input
                required
                type="number"
                step="0.01"
                placeholder="Ej. 10.5"
                className="w-full px-4 py-2 border rounded-md"
                value={newHolding.shares}
                onChange={(e) => setNewHolding({ ...newHolding, shares: e.target.value })}
              />
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio de Compra</label>
              <input
                required
                type="number"
                step="0.01"
                placeholder="Ej. 150.00"
                className="w-full px-4 py-2 border rounded-md"
                value={newHolding.price}
                onChange={(e) => setNewHolding({ ...newHolding, price: e.target.value })}
              />
            </div>
            <Button type="submit" className="w-full md:w-auto h-[42px]">
              Guardar
            </Button>
          </form>
        </Card>
      )}

      {portfolioData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Activo
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio Actual
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rendimiento
                    </th>
                    <th className="px-6 py-3 text-right" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {portfolioData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-bold text-brand-dark">
                        {item.symbol}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">{item.shares}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {formatCurrency(item.currentPrice)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right font-medium">
                        {formatCurrency(item.totalValue)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-right font-bold ${item.gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {formatPercent(item.gainLossPercent)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => removeHolding(item.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          <SafeIcon icon={FiIcons.FiTrash2} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <Card className="p-6 h-full min-h-[300px] flex flex-col">
              <h3 className="text-lg font-bold mb-4">Distribución</h3>
              <div className="flex-1 w-full h-full min-h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar dataKey="Valor" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
          <SafeIcon icon={FiIcons.FiPieChart} className="mx-auto text-5xl text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">Tu portafolio está vacío</h3>
          <p className="text-gray-500 mb-6">
            Agrega tus primeras acciones para comenzar a rastrear su rendimiento.
          </p>
          <Button onClick={() => setShowAdd(true)}>Agregar Activo</Button>
        </div>
      )}
    </div>
  );
}
